import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import Tooltip from '@codegouvfr/react-dsfr/Tooltip';
import clsx from 'clsx';
import { groupBy, isNil, uniq } from 'lodash-es';
import {
  type Department,
  DepartmentLabels
} from 'maestro-shared/referential/Department';
import {
  type Region,
  RegionList,
  Regions
} from 'maestro-shared/referential/Region';
import { hasDepartmentalEchelon } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanEchelon';
import type { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import { stagesFromSubPlans } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import { isDefined } from 'maestro-shared/utils/utils';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import AppToast from '../../../components/_app/AppToast/AppToast';
import ProgrammingPlanDisplayStatusBadge from '../../../components/ProgrammingPlanDisplayStatusBadge/ProgrammingPlanDisplayStatusBadge';
import SelectionCheckbox from '../../../components/SelectionCheckbox/SelectionCheckbox';
import { ApiClientContext } from '../../../services/apiClient';
import ProgrammingPlanBulkLaunchModal, {
  bulkLaunchModal
} from './ProgrammingPlanTrackingActionBar/ProgrammingPlanBulkLaunchModal';
import ProgrammingPlanBulkSendAdminModal, {
  bulkSendAdminModal
} from './ProgrammingPlanTrackingActionBar/ProgrammingPlanBulkSendAdminModal';
import ProgrammingPlanBulkSendDepartmentalModal, {
  bulkSendDepartmentalModal
} from './ProgrammingPlanTrackingActionBar/ProgrammingPlanBulkSendDepartmentalModal';
import ProgrammingPlanBulkSendNationalModal, {
  bulkSendNationalModal
} from './ProgrammingPlanTrackingActionBar/ProgrammingPlanBulkSendNationalModal';
import ProgrammingPlanBulkSendRegionalModal, {
  bulkSendRegionalModal
} from './ProgrammingPlanTrackingActionBar/ProgrammingPlanBulkSendRegionalModal';
import ProgrammingPlanTrackingActionBar from './ProgrammingPlanTrackingActionBar/ProgrammingPlanTrackingActionBar';
import ProgrammingPlanTrackingFilters, {
  emptyTrackingFilters,
  LaunchStatusLabels,
  SettingsStatusLabels,
  type TrackingFilters
} from './ProgrammingPlanTrackingFilters';
import {
  type AggregateDisplayStatus,
  buildEchelonDisplayStatus
} from './ProgrammingPlanTrackingTable.utils';
import {
  sortedDepartments,
  useProgrammingPlanTrackingStatus
} from './useProgrammingPlanTrackingStatus';
import './ProgrammingPlanTrackingTable.scss';

interface Props {
  programmingPlans: ProgrammingPlanChecked[];
  region?: Region;
  department?: Department;
}

const Colgroup = ({ statusColumnCount }: { statusColumnCount: number }) => (
  <colgroup>
    <col className="col-checkbox" />
    <col className="col-name" />
    {Array.from({ length: statusColumnCount }, (_, i) => (
      <col key={`col-status-${i}`} className="col-status" />
    ))}
  </colgroup>
);

const ExpandButton = ({
  isExpanded,
  onClick
}: {
  isExpanded: boolean;
  onClick: () => void;
}) => (
  <Button
    iconId={
      isExpanded ? 'fr-icon-arrow-up-s-line' : 'fr-icon-arrow-down-s-line'
    }
    priority="tertiary no outline"
    size="small"
    title={isExpanded ? 'Réduire' : 'Voir le détail'}
    onClick={onClick}
  />
);

const MiniTable = ({
  statusColumnCount,
  children
}: {
  statusColumnCount: number;
  children: React.ReactNode;
}) => (
  <div
    className={clsx(
      'fr-table',
      'fr-table--bordered',
      'fr-table--no-caption',
      'fr-table--no-scroll'
    )}
  >
    <table>
      <Colgroup statusColumnCount={statusColumnCount} />
      {children}
    </table>
  </div>
);

const ProgrammingPlanTrackingTable = ({
  programmingPlans,
  region,
  department
}: Props) => {
  const apiClient = useContext(ApiClientContext);
  const [expandedPlanIds, setExpandedPlanIds] = useState<Set<string>>(
    new Set()
  );
  const [expandedRegionKeys, setExpandedRegionKeys] = useState<Set<string>>(
    new Set()
  );
  const [selectedPlanIds, setSelectedPlanIds] = useState<Set<string>>(
    new Set()
  );
  const [headerHeight, setHeaderHeight] = useState(0);
  const [bannerHeight, setBannerHeight] = useState(0);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [launchSuccess, setLaunchSuccess] = useState(false);
  const headerWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const header = headerWrapperRef.current;
    if (!header) {
      return;
    }
    const updateHeight = () => setHeaderHeight(header.offsetHeight);
    const ro = new ResizeObserver(updateHeight);
    ro.observe(header);
    updateHeight();
    return () => ro.disconnect();
  }, []);

  const {
    planStatusInfo,
    prescriptionsByPlan,
    localPrescriptionsByPrescription
  } = useProgrammingPlanTrackingStatus(programmingPlans, region, department);

  const hasDepartmentalEchelonPlan = programmingPlans.some((plan) =>
    hasDepartmentalEchelon(plan.distributionKind)
  );

  const { data: programmingPlanDomains } =
    apiClient.useFindProgrammingPlanDomainsQuery();

  const [filters, setFilters] = useState<TrackingFilters>(emptyTrackingFilters);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const { data: coordinators } = apiClient.useFindUsersQuery({
    roles: ['NationalCoordinator'],
    disabled: false
  });

  const columnLabelsOf = useCallback(
    (planId: string) => {
      const info = planStatusInfo.get(planId);
      return {
        national: info?.nationalDisplayStatus.label,
        regional: region
          ? info?.regionalDisplayStatus?.label
          : info?.regionalAggregate.label,
        departmental: department
          ? info?.departmentalDisplayStatus?.label
          : info?.departmentalAggregate?.label
      };
    },
    [planStatusInfo, region, department]
  );

  const matchesCoordinator = useCallback(
    (plan: ProgrammingPlanChecked) => {
      if (!filters.coordinatorIds.length) {
        return true;
      }
      const planStages = stagesFromSubPlans(plan.subPlans);
      return (coordinators ?? [])
        .filter((coordinator) =>
          filters.coordinatorIds.includes(coordinator.id)
        )
        .some((coordinator) =>
          coordinator.stages.some((stage) => planStages.includes(stage))
        );
    },
    [coordinators, filters.coordinatorIds]
  );

  const filterOptions = useMemo(
    () => ({
      domains: (programmingPlanDomains ?? []).filter((domain) =>
        programmingPlans.some((plan) => plan.domainId === domain.id)
      ),
      plans: programmingPlans,
      coordinators: (coordinators ?? []).filter((coordinator) =>
        programmingPlans.some((plan) =>
          stagesFromSubPlans(plan.subPlans).some((stage) =>
            coordinator.stages.includes(stage)
          )
        )
      ),
      nationalStatuses: uniq(
        programmingPlans
          .map((plan) => columnLabelsOf(plan.id).national)
          .filter(isDefined)
      ),
      regionalStatuses: uniq(
        programmingPlans
          .map((plan) => columnLabelsOf(plan.id).regional)
          .filter(isDefined)
      ),
      departmentalStatuses: uniq(
        programmingPlans
          .map((plan) => columnLabelsOf(plan.id).departmental)
          .filter(isDefined)
      )
    }),
    [programmingPlans, programmingPlanDomains, coordinators, columnLabelsOf]
  );

  const displayedPlans = useMemo(
    () =>
      programmingPlans.filter((plan) => {
        const labels = columnLabelsOf(plan.id);
        const matchesStatus = (selected: string[], label?: string) =>
          !selected.length || (!isNil(label) && selected.includes(label));

        return (
          (!filters.programmingPlanDomainIds.length ||
            filters.programmingPlanDomainIds.includes(plan.domainId)) &&
          (!filters.programmingPlanIds.length ||
            filters.programmingPlanIds.includes(plan.id)) &&
          matchesCoordinator(plan) &&
          (!filters.launchStatuses.length ||
            filters.launchStatuses.includes(
              isNil(plan.launchedAt)
                ? LaunchStatusLabels.NotLaunched
                : LaunchStatusLabels.Launched
            )) &&
          (!filters.settingsStatuses.length ||
            filters.settingsStatuses.includes(
              plan.settingsCompleted
                ? SettingsStatusLabels.Completed
                : SettingsStatusLabels.NotCompleted
            )) &&
          matchesStatus(filters.nationalStatuses, labels.national) &&
          matchesStatus(filters.regionalStatuses, labels.regional) &&
          matchesStatus(filters.departmentalStatuses, labels.departmental)
        );
      }),
    [programmingPlans, filters, columnLabelsOf, matchesCoordinator]
  );

  const displayedPlanIds = useMemo(
    () => displayedPlans.map((plan) => plan.id),
    [displayedPlans]
  );

  const selectedPlans = useMemo(
    () => programmingPlans.filter((plan) => selectedPlanIds.has(plan.id)),
    [programmingPlans, selectedPlanIds]
  );

  const plansToAdmin = useMemo(
    () =>
      selectedPlans.filter(
        (plan) => !planStatusInfo.get(plan.id)?.nationalDisplayStatus.modified
      ),
    [selectedPlans, planStatusInfo]
  );

  const plansToRegions = useMemo(
    () =>
      selectedPlans.filter(
        (plan) => planStatusInfo.get(plan.id)?.nationalDisplayStatus.modified
      ),
    [selectedPlans, planStatusInfo]
  );

  const selectedRegionalModified = useMemo(
    () =>
      selectedPlans.length > 0 &&
      selectedPlans.every(
        (plan) => planStatusInfo.get(plan.id)?.regionalDisplayStatus?.modified
      ),
    [selectedPlans, planStatusInfo]
  );
  const selectedDepartmentalModified = useMemo(
    () =>
      selectedPlans.length > 0 &&
      selectedPlans.every(
        (plan) =>
          planStatusInfo.get(plan.id)?.departmentalDisplayStatus?.modified
      ),
    [selectedPlans, planStatusInfo]
  );
  const departmentalActionLabel = selectedDepartmentalModified
    ? 'Diffuser les modifications aux préleveurs'
    : 'Diffuser les plans aux préleveurs';

  const selectedAllRegionalKind =
    selectedPlans.length > 0 &&
    selectedPlans.every(
      (plan) => !hasDepartmentalEchelon(plan.distributionKind)
    );
  const regionalActionLabel = selectedAllRegionalKind
    ? selectedRegionalModified
      ? 'Diffuser les modifications aux préleveurs'
      : 'Diffuser les plans aux préleveurs'
    : 'Soumettre les plans aux départements';

  const handleSendSuccess = () => {
    setSelectedPlanIds(new Set());
    setSendSuccess(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const plansToRegionsByAdmin = useMemo(
    () =>
      selectedPlans.filter(
        (plan) => plan.nationalStatus.status === 'SubmittedToAdmin'
      ),
    [selectedPlans]
  );

  const plansToLaunch = useMemo(
    () => selectedPlans.filter((plan) => isNil(plan.launchedAt)),
    [selectedPlans]
  );

  const handleLaunchSuccess = () => {
    setSelectedPlanIds(new Set());
    setLaunchSuccess(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isSelectable = (planId: string) => {
    const info = planStatusInfo.get(planId);
    return info?.isEligible === true || info?.isLaunchable === true;
  };

  const toggleSelectionForScope = (scopePlanIds: string[]) => {
    const eligibleInScope = scopePlanIds.filter(isSelectable);
    setSelectedPlanIds((prev) => {
      const allSelected =
        eligibleInScope.length > 0 &&
        eligibleInScope.every((id) => prev.has(id));
      const next = new Set(prev);
      for (const id of eligibleInScope) {
        if (allSelected) {
          next.delete(id);
        } else {
          next.add(id);
        }
      }
      return next;
    });
  };

  const togglePlanSelection = (planId: string) =>
    setSelectedPlanIds((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) {
        next.delete(planId);
      } else {
        next.add(planId);
      }
      return next;
    });

  const getScopeSelectionState = (scopePlanIds: string[]) => {
    const eligibleInScope = scopePlanIds.filter(isSelectable);
    const selectedCount = eligibleInScope.filter((id) =>
      selectedPlanIds.has(id)
    ).length;
    return {
      checked:
        eligibleInScope.length > 0 && selectedCount === eligibleInScope.length,
      indeterminate:
        selectedCount > 0 && selectedCount < eligibleInScope.length,
      disabled: eligibleInScope.length === 0
    };
  };

  const togglePlan = (planId: string) =>
    setExpandedPlanIds((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) {
        next.delete(planId);
      } else {
        next.add(planId);
      }
      return next;
    });

  const toggleRegion = (planId: string, region: string) =>
    setExpandedRegionKeys((prev) => {
      const key = `${planId}:${region}`;
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const domainGroups = useMemo(
    () => groupBy(displayedPlans, 'domainId'),
    [displayedPlans]
  );

  const statusColumnCount =
    1 +
    (region ? 0 : 1) +
    (department ? 0 : 1) +
    (hasDepartmentalEchelonPlan ? 1 : 0);

  if (!programmingPlanDomains) {
    return null;
  }

  return (
    <>
      <AppToast
        open={sendSuccess}
        description="Les plans sélectionnés ont bien été envoyés."
        onClose={() => setSendSuccess(false)}
      />
      <AppToast
        open={launchSuccess}
        description="La campagne a bien été lancée sur les plans sélectionnés."
        onClose={() => setLaunchSuccess(false)}
      />
      <div
        className={cx('fr-container', 'fr-px-5w', 'fr-mb-2w', 'fr-mb-md-3w')}
      >
        <h4 className={cx('fr-mb-1w')}>Suivi des plans</h4>
      </div>
      <ProgrammingPlanTrackingFilters
        filters={filters}
        onChange={setFilters}
        options={filterOptions}
        isExpanded={isFilterExpanded}
        onToggleExpanded={() => setIsFilterExpanded((prev) => !prev)}
        region={region}
        department={department}
      />
      <ProgrammingPlanTrackingActionBar
        selectedCount={selectedPlanIds.size}
        onDeselectAll={() => setSelectedPlanIds(new Set())}
        onOpenAdminModal={() => bulkSendAdminModal.open()}
        onOpenLaunchModal={() => bulkLaunchModal.open()}
        isLaunchDisabled={plansToLaunch.length === 0}
        isAdminSendDisabled={plansToRegionsByAdmin.length === 0}
        onOpenNationalModal={() => bulkSendNationalModal.open()}
        onOpenRegionalModal={() => bulkSendRegionalModal.open()}
        onOpenDepartmentalModal={() => bulkSendDepartmentalModal.open()}
        regionalActionLabel={regionalActionLabel}
        departmentalActionLabel={departmentalActionLabel}
        onHeightChange={setBannerHeight}
      />
      <ProgrammingPlanBulkSendAdminModal
        plans={plansToRegionsByAdmin}
        onSuccess={handleSendSuccess}
      />
      <ProgrammingPlanBulkLaunchModal
        plans={plansToLaunch}
        onSuccess={handleLaunchSuccess}
      />
      <ProgrammingPlanBulkSendNationalModal
        plansToAdmin={plansToAdmin}
        plansToRegions={plansToRegions}
        onSuccess={handleSendSuccess}
      />
      <ProgrammingPlanBulkSendDepartmentalModal
        plans={selectedPlans}
        modified={selectedDepartmentalModified}
        onSuccess={handleSendSuccess}
      />
      <ProgrammingPlanBulkSendRegionalModal
        plans={selectedPlans}
        modified={selectedRegionalModified}
        onSuccess={handleSendSuccess}
      />
      <div className="programming-plan-tracking-table">
        <div
          className="header-wrapper"
          style={{ top: bannerHeight }}
          ref={headerWrapperRef}
        >
          <MiniTable statusColumnCount={statusColumnCount}>
            <thead>
              <tr>
                <th scope="col">
                  <SelectionCheckbox
                    variant="header"
                    {...getScopeSelectionState(displayedPlanIds)}
                    onChange={() => toggleSelectionForScope(displayedPlanIds)}
                  />
                </th>
                <th scope="col">Nom du plan</th>
                <th scope="col" className="launch-cell">
                  Lancement national
                </th>
                {!region && <th scope="col">Statut BGIR</th>}
                {!department && <th scope="col">Statut région</th>}
                {hasDepartmentalEchelonPlan && (
                  <th scope="col">Statut département</th>
                )}
              </tr>
            </thead>
          </MiniTable>
        </div>

        {Object.entries(domainGroups).map(([domainId, plansInDomain]) => {
          const domainPlanIds = plansInDomain.map((plan) => plan.id);

          return (
            <Fragment key={`domain-${domainId}`}>
              <div
                className="plan-group-sticky-container"
                style={{ top: bannerHeight + headerHeight }}
              >
                <MiniTable statusColumnCount={statusColumnCount}>
                  <tbody>
                    <tr className="plan-group-header-row">
                      <td>
                        <SelectionCheckbox
                          variant="header"
                          {...getScopeSelectionState(domainPlanIds)}
                          onChange={() =>
                            toggleSelectionForScope(domainPlanIds)
                          }
                        />
                      </td>
                      <td colSpan={statusColumnCount + 1}>
                        <div className="plan-group-title">
                          {
                            programmingPlanDomains?.find(
                              (_) => _.id === domainId
                            )?.label
                          }
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </MiniTable>
              </div>

              <MiniTable statusColumnCount={statusColumnCount}>
                <tbody>
                  {plansInDomain.map((plan) => {
                    const planPrescriptions =
                      prescriptionsByPlan[plan.id] ?? [];
                    const planLocalPrescriptions = planPrescriptions.flatMap(
                      (_) => localPrescriptionsByPrescription[_.id] ?? []
                    );

                    const {
                      nationalDisplayStatus,
                      regionalDisplayStatus,
                      departmentalDisplayStatus,
                      isEligible,
                      isLaunchable,
                      regionalAggregate,
                      departmentalAggregate
                    } = planStatusInfo.get(plan.id)!;

                    const isPlanExpanded = expandedPlanIds.has(plan.id);
                    const canExpandDepartments =
                      hasDepartmentalEchelon(plan.distributionKind) &&
                      !department;

                    const relevantStatus = department
                      ? departmentalDisplayStatus
                      : region
                        ? regionalDisplayStatus
                        : nationalDisplayStatus;
                    const isRepartitionIncomplete =
                      !isEligible &&
                      !isLaunchable &&
                      relevantStatus?.value === 'InProgress';
                    const checkbox = (
                      <SelectionCheckbox
                        checked={selectedPlanIds.has(plan.id)}
                        disabled={!isSelectable(plan.id)}
                        onChange={() => togglePlanSelection(plan.id)}
                      />
                    );

                    return (
                      <Fragment key={plan.id}>
                        <tr>
                          <td>
                            {isRepartitionIncomplete ? (
                              <Tooltip
                                kind="hover"
                                title="Vous ne pouvez pas envoyer ce plan car la répartition n'est pas complète"
                              >
                                {checkbox}
                              </Tooltip>
                            ) : (
                              checkbox
                            )}
                          </td>
                          <td>
                            <div className="row-reference">
                              {plan.title}
                              {(!region || canExpandDepartments) && (
                                <ExpandButton
                                  isExpanded={isPlanExpanded}
                                  onClick={() => togglePlan(plan.id)}
                                />
                              )}
                            </div>
                          </td>
                          <td className="launch-cell">
                            {!isNil(plan.launchedAt) && (
                              <span
                                className={clsx(
                                  cx(
                                    'fr-icon-checkbox-circle-fill',
                                    'fr-label--success'
                                  )
                                )}
                                title="Campagne lancée"
                              />
                            )}
                          </td>
                          {!region && (
                            <td>
                              <ProgrammingPlanDisplayStatusBadge
                                result={nationalDisplayStatus}
                                showDates
                                small
                              />
                            </td>
                          )}
                          {!department && (
                            <td>
                              {region ? (
                                regionalDisplayStatus ? (
                                  <ProgrammingPlanDisplayStatusBadge
                                    result={regionalDisplayStatus}
                                    showDates
                                    small
                                  />
                                ) : (
                                  '—'
                                )
                              ) : (
                                <AggregateBadge aggregate={regionalAggregate} />
                              )}
                            </td>
                          )}
                          {hasDepartmentalEchelonPlan && (
                            <td>
                              {department ? (
                                departmentalDisplayStatus ? (
                                  <ProgrammingPlanDisplayStatusBadge
                                    result={departmentalDisplayStatus}
                                    showDates
                                    small
                                  />
                                ) : (
                                  <span className={cx('fr-text--sm')}>N/A</span>
                                )
                              ) : departmentalAggregate &&
                                departmentalAggregate.value !==
                                  'NotApplicable' ? (
                                <AggregateBadge
                                  aggregate={departmentalAggregate}
                                />
                              ) : departmentalAggregate &&
                                regionalAggregate.value === 'Pending' ? (
                                <AggregateBadge
                                  aggregate={{
                                    value: 'Pending',
                                    label: 'En attente'
                                  }}
                                />
                              ) : (
                                <span className={cx('fr-text--sm')}>N/A</span>
                              )}
                            </td>
                          )}
                        </tr>

                        {isPlanExpanded &&
                          region &&
                          canExpandDepartments &&
                          sortedDepartments(region).map((department) => {
                            const departmentResult = buildEchelonDisplayStatus(
                              plan,
                              planPrescriptions,
                              planLocalPrescriptions,
                              'Departmental',
                              region,
                              department
                            );
                            return (
                              <tr
                                key={`${plan.id}:${region}:${department}`}
                                className="department-sub-row"
                              >
                                <td colSpan={statusColumnCount + 2}>
                                  <div className="sub-row-bar">
                                    <span className="department-name">
                                      {DepartmentLabels[department]} (
                                      {department})
                                    </span>
                                    <ProgrammingPlanDisplayStatusBadge
                                      result={departmentResult}
                                      small
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}

                        {isPlanExpanded &&
                          !region &&
                          RegionList.map((regionColumn) => {
                            const regionResult = buildEchelonDisplayStatus(
                              plan,
                              planPrescriptions,
                              planLocalPrescriptions,
                              'Regional',
                              regionColumn
                            );
                            const regionKey = `${plan.id}:${regionColumn}`;
                            const isRegionExpanded =
                              expandedRegionKeys.has(regionKey);

                            const departmentResultsForRegion =
                              canExpandDepartments
                                ? sortedDepartments(regionColumn).map(
                                    (department) =>
                                      buildEchelonDisplayStatus(
                                        plan,
                                        planPrescriptions,
                                        planLocalPrescriptions,
                                        'Departmental',
                                        regionColumn,
                                        department
                                      )
                                  )
                                : [];
                            const submittedDepartmentsCount =
                              departmentResultsForRegion.filter(
                                (_) => _.value === 'Submitted'
                              ).length;

                            return (
                              <Fragment key={regionKey}>
                                <tr
                                  className={clsx(
                                    'region-sub-row',
                                    isRegionExpanded && 'is-expanded'
                                  )}
                                >
                                  <td colSpan={statusColumnCount + 2}>
                                    <div className="sub-row-bar">
                                      <div className="row-reference">
                                        <span className="region-name">
                                          {Regions[regionColumn].name}
                                        </span>
                                        {canExpandDepartments && (
                                          <span
                                            className={clsx(
                                              cx('fr-text--xs'),
                                              'sub-count'
                                            )}
                                          >
                                            {submittedDepartmentsCount}/
                                            {departmentResultsForRegion.length}{' '}
                                            départements
                                          </span>
                                        )}
                                      </div>
                                      <div className="row-reference">
                                        <ProgrammingPlanDisplayStatusBadge
                                          result={regionResult}
                                          small
                                        />
                                        {canExpandDepartments && (
                                          <ExpandButton
                                            isExpanded={isRegionExpanded}
                                            onClick={() =>
                                              toggleRegion(
                                                plan.id,
                                                regionColumn
                                              )
                                            }
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                </tr>

                                {isRegionExpanded &&
                                  canExpandDepartments &&
                                  sortedDepartments(regionColumn).map(
                                    (department) => {
                                      const departmentResult =
                                        buildEchelonDisplayStatus(
                                          plan,
                                          planPrescriptions,
                                          planLocalPrescriptions,
                                          'Departmental',
                                          regionColumn,
                                          department
                                        );
                                      return (
                                        <tr
                                          key={`${regionKey}:${department}`}
                                          className="department-sub-row"
                                        >
                                          <td colSpan={statusColumnCount + 2}>
                                            <div className="sub-row-bar">
                                              <span className="department-name">
                                                {DepartmentLabels[department]} (
                                                {department})
                                              </span>
                                              <ProgrammingPlanDisplayStatusBadge
                                                result={departmentResult}
                                                small
                                              />
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    }
                                  )}
                              </Fragment>
                            );
                          })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </MiniTable>
            </Fragment>
          );
        })}
      </div>
    </>
  );
};

const AggregateBadge = ({
  aggregate
}: {
  aggregate: AggregateDisplayStatus;
}) => {
  if (aggregate.value === 'NotApplicable') {
    return <span className={cx('fr-text--sm')}>N/A</span>;
  }
  const severity =
    aggregate.value === 'Submitted'
      ? 'success'
      : aggregate.value === 'InProgress'
        ? 'new'
        : aggregate.value === 'Pending'
          ? undefined
          : 'warning';
  return (
    <span
      className={cx(
        'fr-badge',
        severity && `fr-badge--${severity}`,
        'fr-badge--sm',
        'fr-badge--no-icon'
      )}
    >
      {aggregate.ratio
        ? `${aggregate.label} (${aggregate.ratio.sent}/${aggregate.ratio.total})`
        : aggregate.label}
    </span>
  );
};

export default ProgrammingPlanTrackingTable;
